import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { AlertTriangle, ArrowRight, ChevronDown, Info, X } from 'lucide-react'
import { ETICHETE_FAZA, ETICHETE_TIP, esteNecesarTeren, type FazaActivitate } from '@b4l/shared'
import { api } from '../../../lib/api'
import { useAuthStore } from '../../../stores/auth'
import { Pastila } from '../../ui/Pastila'
import { TON } from '../../ui/TonuriB4L'
import { RezumatActivitate } from './RezumatActivitate'
import { PatratTip, TON_FAZA, candRelativ, cuFaza, lei } from './activitate'
import { CULOARE_STARE_CERERE, ETICHETA_STARE, type Cerere } from './tipuriNecesar'
import { eroare, type Campanie } from './comun'
import { FormularCerere } from './FormularCerere'

interface LinieContext {
  linieId: number
  productId: number
  sku: string
  denumire: string
  cerut: number
  aprobat: number | null
  pret: number | null
  valoare: number | null
  stocMostre: number | null
  stocDepozit: number | null
  rezervatAltele: number
}
interface Context {
  linii: LinieContext[]
  total: number
  faraPret: number
  avertismente: { ton: 'rosu' | 'galben' | 'info'; text: string }[]
}
interface Sumar {
  faza: FazaActivitate
  plata: Campanie['plata']
  plataMotiv: string | null
  areMostre: boolean
  cereriDeAprobat: number
  actiuniTeren: number
  angajat: number
  cheltuit: number
  cerereUitata?: boolean
}

function cui(c: Cerere): string {
  if (c.tip === 'colaborator')
    return `către colaboratorul ${c.influencer?.handle ?? c.livrare?.nume ?? '—'}`
  if (c.tip === 'partener') return `către partenerul ${c.client?.name ?? c.livrare?.nume ?? '—'}`
  return c.actiune
    ? `pentru acțiunea „${c.actiune.denumire}”`
    : 'pentru activitate, fără o acțiune anume'
}

function zi(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('ro-RO', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/**
 * Aprobarea unei cereri de marfă, cu activitatea alături (Madalin, 15.10.2026: „să poată vedea și
 * summary-ul, ca să știe exact despre ce eveniment e vorba înainte de a aproba sau respinge").
 *
 * În stânga, rezumatul activității — aceeași piesă ca pe pagina ei și în listă. În dreapta,
 * cererea: pentru ce, liniile cu stocul liber, ce e promis altora și valoarea, avertismentele
 * întâi, apoi decizia. Aprobă doar un admin (o oprește și serverul); ceilalți văd tot, fără butoane.
 */
export function PanouAprobareCerere({
  cerere,
  onInchide,
  onDecis,
}: {
  cerere: Cerere
  onInchide: () => void
  /** După aprobare sau respingere — lista trece la următoarea cerere. */
  onDecis?: () => void
}) {
  const qc = useQueryClient()
  const poateAproba = useAuthStore((s) => s.permissions).includes('marketing.necesar:aproba')
  const deDecis = cerere.status === 'trimisa'
  /*
    Ciorna sau cererea trimisă înapoi, deschisă de autorul ei din „Cereri de marfă” (26.10.2026):
    de aici o corectează și o trimite, fără să caute activitatea. Serverul verifică oricum autorul.
  */
  const euId = useAuthStore((s) => s.user?.id)
  const deTrimis =
    (cerere.status === 'ciorna' || cerere.status === 'returnata') && cerere.ceruteDeId === euId
  const [editez, setEditez] = useState(false)
  const trimite = useMutation({
    mutationFn: async () => (await api.post(`/marketing/necesar/cerere/${cerere.id}/trimite`)).data,
    onSuccess: () => {
      toast.success(`Cererea #${cerere.id} a fost trimisă.`)
      void qc.invalidateQueries({ queryKey: ['necesar'] })
    },
    onError: eroare,
  })
  const [cant, setCant] = useState<Record<number, number>>({})
  const [nota, setNota] = useState('')
  const [rezumatDeschis, setRezumatDeschis] = useState(false)

  useEffect(() => {
    setCant({})
    setNota('')
  }, [cerere.id])
  useEffect(() => {
    const k = (e: KeyboardEvent) => e.key === 'Escape' && onInchide()
    window.addEventListener('keydown', k)
    return () => window.removeEventListener('keydown', k)
  }, [onInchide])

  const ctx = useQuery<Context>({
    queryKey: ['necesar', 'context', cerere.id],
    queryFn: async () => (await api.get(`/marketing/necesar/cerere/${cerere.id}/context`)).data,
  })
  const camp = useQuery<{ campanie: Campanie }>({
    queryKey: ['campanie', cerere.campanieId],
    queryFn: async () => (await api.get(`/campanii/${cerere.campanieId}`)).data,
  })
  const sumar = useQuery<{ summary: Sumar }>({
    queryKey: ['campanie', cerere.campanieId, 'summary'],
    queryFn: async () => (await api.get(`/campanii/${cerere.campanieId}/summary`)).data,
  })
  const alte = useQuery<{ cereri: Cerere[] }>({
    queryKey: ['necesar', cerere.campanieId],
    queryFn: async () => (await api.get(`/marketing/necesar/${cerere.campanieId}`)).data,
  })

  const activitate = useMemo(() => {
    const c = camp.data?.campanie
    if (!c) return null
    const s = sumar.data?.summary
    const b = cuFaza(c)
    return {
      ...b,
      faza: s?.faza ?? b.faza,
      costTotal: s?.cheltuit,
      angajat: s?.angajat,
      plata: s?.plata,
      plataMotiv: s?.plataMotiv,
      areMostre: s?.areMostre,
      cereriDeAprobat: s?.cereriDeAprobat,
      cerereUitata: s?.cerereUitata,
      _count: { actiuniSampling: s?.actiuniTeren ?? 0 },
    }
  }, [camp.data, sumar.data])

  const linii = ctx.data?.linii ?? []
  // Cantitatea care se aproba: implicit cea ceruta; nu se poate mari (o opreste si serverul).
  const aprob = (l: LinieContext) => cant[l.linieId] ?? l.cerut
  const reduse = linii.filter((l) => aprob(l) < l.cerut).length
  const toateZero = linii.length > 0 && linii.every((l) => aprob(l) === 0)
  const valoareAprobata = linii.reduce((s, l) => s + (l.pret == null ? 0 : l.pret * aprob(l)), 0)

  const ceruteDe = cerere.ceruteDe?.name ?? cerere.ceruteDe?.email ?? '—'
  const decide = useMutation({
    mutationFn: async (v: { ce: 'aproba' | 'respinge' | 'trimite-inapoi'; corp: unknown }) =>
      (await api.post(`/marketing/necesar/cerere/${cerere.id}/${v.ce}`, v.corp)).data,
    onSuccess: (_d, v) => {
      toast.success(
        v.ce === 'respinge'
          ? `Cererea #${cerere.id} a fost respinsă.`
          : v.ce === 'trimite-inapoi'
            ? `Cererea #${cerere.id} a fost trimisă înapoi lui ${ceruteDe}.`
            : toateZero
              ? `Cererea #${cerere.id} a fost închisă fără marfă.`
              : reduse
                ? `Cererea #${cerere.id} a fost aprobată, cu ${reduse} ${reduse === 1 ? 'cantitate redusă' : 'cantități reduse'}.`
                : `Cererea #${cerere.id} a fost aprobată.`,
      )
      void qc.invalidateQueries({ queryKey: ['necesar'] })
      void qc.invalidateQueries({ queryKey: ['campanie', cerere.campanieId] })
      void qc.invalidateQueries({ queryKey: ['campanii'] })
      onDecis?.()
    },
    onError: eroare,
  })

  const alteCereri = (alte.data?.cereri ?? []).filter((c) => c.id !== cerere.id)

  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/35" onClick={onInchide} aria-hidden />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label={`Cererea de marfă #${cerere.id}`}
        className="absolute inset-y-0 right-0 flex w-full max-w-[1000px] flex-col bg-b4l-surface shadow-2xl motion-safe:animate-[panou_.18s_ease-out]"
      >
        <header className="flex items-start justify-between gap-3 border-b border-b4l-border px-5 pb-3 pt-4">
          <div className="grid min-w-0 gap-0.5">
            <span className="text-mic text-b4l-text-dim">
              Cererea de marfă #{cerere.id} · {ceruteDe}, {zi(cerere.trimisaLa ?? cerere.createdAt)}
            </span>
            <h2 className="flex flex-wrap items-center gap-2 text-sectiune font-semibold text-b4l-text">
              Marfă pentru {cerere.campanie.titlu}
              <span
                className={`rounded-full px-2 py-px text-[11.5px] font-semibold ${CULOARE_STARE_CERERE[cerere.status] ?? ''}`}
              >
                {ETICHETA_STARE[cerere.status] ?? cerere.status}
              </span>
            </h2>
          </div>
          <button
            type="button"
            onClick={onInchide}
            aria-label="Închide"
            className="rounded p-1 text-b4l-text-muted hover:bg-b4l-bg"
          >
            <X size={18} />
          </button>
        </header>

        <div className="grid min-h-0 flex-1 content-start overflow-y-auto lg:grid-cols-[340px_minmax(0,1fr)] lg:content-stretch lg:overflow-hidden">
          {/* Activitatea — pe telefon restrânsă deasupra, pe calculator în stânga, mereu deschisă. */}
          <section
            aria-label="Activitatea"
            className="grid content-start gap-3 border-b border-b4l-border bg-b4l-bg-soft p-4 lg:overflow-y-auto lg:border-b-0 lg:border-r"
          >
            <div className="flex flex-wrap items-center gap-2">
              <PatratTip
                tip={cerere.campanie.tip as never}
                eticheta={ETICHETE_TIP[cerere.campanie.tip as never] ?? ''}
                mare={false}
              />
              <span className="text-[11px] font-semibold uppercase tracking-wider text-b4l-text-dim">
                Activitatea
              </span>
              {activitate && (
                <>
                  <Pastila ton={TON_FAZA[activitate.faza]}>
                    {ETICHETE_FAZA[activitate.faza]}
                  </Pastila>
                  <span className="text-mic text-b4l-text-dim">{candRelativ(activitate)}</span>
                </>
              )}
              <button
                type="button"
                onClick={() => setRezumatDeschis(!rezumatDeschis)}
                aria-expanded={rezumatDeschis}
                className="ml-auto inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-mic text-b4l-blue-dark lg:hidden"
              >
                {rezumatDeschis ? 'Ascunde' : 'Detalii'}
                <ChevronDown size={14} className={rezumatDeschis ? 'rotate-180' : ''} />
              </button>
            </div>
            <div className={`${rezumatDeschis ? 'grid' : 'hidden'} gap-3 lg:grid`}>
              {camp.isLoading ? (
                <p className="text-mic text-b4l-text-muted">Se încarcă…</p>
              ) : !activitate ? (
                <p className="text-mic text-b4l-red-dark">Activitatea nu s-a putut încărca.</p>
              ) : (
                <RezumatActivitate a={activitate} numeResponsabil={null} />
              )}
              {alteCereri.length > 0 && (
                <div className="grid gap-1.5">
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-b4l-text-dim">
                    Alte cereri pentru ea
                  </span>
                  {alteCereri.slice(0, 6).map((c) => (
                    <span key={c.id} className="flex items-center justify-between gap-2 text-mic">
                      <span className="truncate">
                        #{c.id} · {c.linii.length} {c.linii.length === 1 ? 'produs' : 'produse'}
                      </span>
                      <span
                        className={`rounded-full px-2 py-px text-[11px] ${CULOARE_STARE_CERERE[c.status] ?? ''}`}
                      >
                        {ETICHETA_STARE[c.status] ?? c.status}
                      </span>
                    </span>
                  ))}
                </div>
              )}
              <Link
                to={`/marketing/evenimente/${cerere.campanieId}?fila=marfa&vedere=cereri&cerere=${cerere.id}`}
                className="inline-flex items-center gap-1 justify-self-start text-mic text-b4l-blue-dark hover:underline"
              >
                Deschide activitatea <ArrowRight size={13} />
              </Link>
            </div>
          </section>

          {/* Cererea și decizia */}
          <section aria-label="Cererea" className="grid content-start gap-4 p-4 lg:overflow-y-auto">
            <div className="grid gap-1">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-b4l-text-dim">
                Pentru ce
              </span>
              <p className="text-corp text-b4l-text">
                {cerere.motiv ? (
                  `„${cerere.motiv}”`
                ) : (
                  <span className="text-b4l-text-muted">Fără motiv scris.</span>
                )}
              </p>
              <p className="text-mic text-b4l-text-muted">{cui(cerere)}</p>
              {/*
                PRIN CURIER: aprobatorul vede EXACT unde pleaca AWB-ul, inainte sa aprobe — nu dupa,
                pe colet (16.10.2026).
              */}
              {cerere.prinCurier && cerere.livrare && (
                <div className="mt-1 rounded border border-b4l-purple/30 bg-b4l-purple-light/40 px-2 py-1.5 text-mic text-b4l-text">
                  <span className="font-semibold text-b4l-purple-dark">
                    Prin curier, după aprobare:{' '}
                  </span>
                  {[
                    cerere.tip === 'partener' ? cerere.livrare.nume : null,
                    cerere.livrare.contact,
                    cerere.livrare.telefon,
                    cerere.livrare.email,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                  <br />
                  {[
                    [cerere.livrare.strada, cerere.livrare.numar].filter(Boolean).join(' '),
                    [
                      cerere.livrare.bloc && `bl. ${cerere.livrare.bloc}`,
                      cerere.livrare.scara && `sc. ${cerere.livrare.scara}`,
                      cerere.livrare.etaj && `et. ${cerere.livrare.etaj}`,
                      cerere.livrare.apartament && `ap. ${cerere.livrare.apartament}`,
                    ]
                      .filter(Boolean)
                      .join(' '),
                    cerere.livrare.localitate,
                    cerere.livrare.judet,
                    cerere.livrare.codPostal,
                  ]
                    .filter(Boolean)
                    .join(', ')}
                </div>
              )}
              {cerere.motivRespingere &&
                (cerere.status === 'returnata' ? (
                  <p className="text-mic text-b4l-amber-dark">
                    Trimisă înapoi: {cerere.motivRespingere}
                  </p>
                ) : (
                  <p className="text-mic text-b4l-red-dark">Respinsă: {cerere.motivRespingere}</p>
                ))}
            </div>

            {ctx.isLoading && (
              <p className="text-mic text-b4l-text-muted">Se verifică stocul și valoarea…</p>
            )}
            {ctx.isError && (
              <p className="text-mic text-b4l-red-dark">
                Stocul și valoarea nu s-au putut încărca.{' '}
                <button type="button" onClick={() => void ctx.refetch()} className="underline">
                  Reîncearcă
                </button>
              </p>
            )}

            {deDecis &&
              (ctx.data?.avertismente ?? []).map((a) => (
                <div
                  key={a.text}
                  className={`grid grid-cols-[18px_minmax(0,1fr)] gap-2 rounded-lg px-3 py-2 text-mic ${
                    a.ton === 'rosu'
                      ? TON.rosu.pastila
                      : a.ton === 'galben'
                        ? TON.galben.pastila
                        : 'bg-b4l-bg text-b4l-text-muted'
                  }`}
                >
                  {a.ton === 'info' ? <Info size={15} /> : <AlertTriangle size={15} />}
                  <span>{a.text}</span>
                </div>
              ))}

            {ctx.isSuccess && linii.length === 0 && (
              <p className="rounded-lg bg-b4l-bg px-3 py-2 text-mic text-b4l-text-muted">
                Cererea nu are niciun produs — n-ai ce aproba. Respinge-o, cu o notă.
              </p>
            )}
            {linii.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-corp">
                  <caption className="sr-only">
                    Produsele cerute, cu stocul liber și valoarea
                  </caption>
                  <thead>
                    <tr className="text-left text-[11px] uppercase tracking-wider text-b4l-text-dim">
                      <th className="py-2 pr-2 font-semibold">Produs</th>
                      <th className="px-2 text-right font-semibold">Cerut</th>
                      <th
                        className="px-2 text-right font-semibold"
                        title="Stoc liber în gestiunea Mostre (7), din ExpertAccounts"
                      >
                        Mostre
                      </th>
                      <th
                        className="px-2 text-right font-semibold"
                        title="Stoc liber în Depozit (1), din ExpertAccounts"
                      >
                        Depozit
                      </th>
                      <th className="px-2 text-right font-semibold">
                        {deDecis ? 'De aprobat' : 'Aprobat'}
                      </th>
                      <th className="pl-2 text-right font-semibold">Valoare</th>
                    </tr>
                  </thead>
                  <tbody>
                    {linii.map((l) => {
                      const liber =
                        l.stocMostre == null || l.stocDepozit == null
                          ? null
                          : l.stocMostre + l.stocDepozit - l.rezervatAltele
                      const a = aprob(l)
                      return (
                        <tr key={l.linieId} className="border-t border-b4l-border align-top">
                          <td className="py-2 pr-2">
                            {l.denumire}
                            {l.rezervatAltele > 0 && (
                              <span className="block text-mic text-b4l-text-dim">
                                {l.rezervatAltele} promise altor cereri
                              </span>
                            )}
                          </td>
                          <td className="px-2 py-2 text-right tabular-nums">{l.cerut}</td>
                          <td className="px-2 py-2 text-right tabular-nums">
                            {l.stocMostre ?? <span className="text-b4l-text-dim">?</span>}
                          </td>
                          <td className="px-2 py-2 text-right tabular-nums">
                            {l.stocDepozit ?? <span className="text-b4l-text-dim">?</span>}
                          </td>
                          <td className="px-2 py-1.5 text-right">
                            {deDecis && poateAproba ? (
                              <input
                                type="number"
                                min={0}
                                max={l.cerut}
                                value={a}
                                disabled={decide.isPending}
                                aria-label={`Cantitate aprobată ${l.denumire}`}
                                onChange={(e) => {
                                  const v = Math.max(
                                    0,
                                    Math.min(l.cerut, Math.floor(Number(e.target.value) || 0)),
                                  )
                                  setCant((c) => ({ ...c, [l.linieId]: v }))
                                }}
                                className={`w-16 rounded-md border px-1.5 py-1 text-right tabular-nums ${
                                  a < l.cerut
                                    ? 'border-b4l-amber bg-b4l-amber-light'
                                    : liber != null && a > liber
                                      ? 'border-b4l-red'
                                      : 'border-b4l-border bg-b4l-surface'
                                }`}
                              />
                            ) : (
                              <span className="tabular-nums">{l.aprobat ?? '—'}</span>
                            )}
                          </td>
                          <td className="py-2 pl-2 text-right tabular-nums">
                            {l.pret == null ? (
                              <span className="text-b4l-text-dim">fără preț</span>
                            ) : (
                              lei(l.pret * (deDecis ? a : (l.aprobat ?? l.cerut)))
                            )}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="border-t border-b4l-border">
                      <td colSpan={5} className="py-2 text-right text-mic text-b4l-text-muted">
                        {deDecis ? 'Valoarea aprobată' : 'Valoare'}
                        {ctx.data?.faraPret ? ' (cel puțin)' : ''}
                      </td>
                      <td className="py-2 pl-2 text-right font-semibold tabular-nums">
                        {lei(deDecis ? valoareAprobata : (ctx.data?.total ?? 0))}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}

            {deDecis && poateAproba && (
              <label className="grid gap-1">
                <span className="text-[11px] font-semibold uppercase tracking-wider text-b4l-text-dim">
                  Notă pentru {ceruteDe}
                  <span className="ml-1 font-normal normal-case tracking-normal">
                    · obligatorie la „Trimite înapoi”, „Respinge” și când nu dai nimic
                  </span>
                </span>
                <textarea
                  value={nota}
                  onChange={(e) => setNota(e.target.value)}
                  rows={2}
                  maxLength={2000}
                  placeholder="ex.: creatina — 8 ajung pentru tombolă"
                  className="rounded-caseta border border-b4l-border bg-b4l-surface px-2.5 py-2 text-corp"
                />
              </label>
            )}
            {deDecis && !poateAproba && (
              <p className="rounded-lg bg-b4l-bg px-3 py-2 text-mic text-b4l-text-muted">
                Cererea așteaptă un admin. Vezi tot, dar decizia e a lui.
              </p>
            )}
          </section>
        </div>

        {deTrimis && (
          <footer className="flex flex-wrap items-center justify-end gap-2 border-t border-b4l-border px-5 py-3">
            <button
              type="button"
              onClick={() => setEditez(true)}
              className="rounded-caseta border border-b4l-border px-3 py-1.5 text-corp hover:border-b4l-blue"
            >
              Editează
            </button>
            <button
              type="button"
              disabled={trimite.isPending}
              onClick={() => trimite.mutate()}
              className="rounded-caseta bg-b4l-green px-3.5 py-1.5 text-corp font-semibold text-white hover:opacity-90 disabled:opacity-40"
            >
              {cerere.status === 'returnata' ? 'Trimite din nou' : 'Trimite cererea'}
            </button>
          </footer>
        )}
        {editez && (
          <FormularCerere
            campanieId={cerere.campanieId}
            initial={cerere}
            teren={esteNecesarTeren(cerere.campanie.cod)}
            onClose={() => setEditez(false)}
            onGata={() => void qc.invalidateQueries({ queryKey: ['necesar'] })}
          />
        )}

        {deDecis && poateAproba && (
          <footer className="flex flex-wrap items-center justify-end gap-2 border-t border-b4l-border px-5 py-3">
            <button
              type="button"
              disabled={decide.isPending || !nota.trim()}
              title={nota.trim() ? undefined : 'Scrie întâi de ce o respingi'}
              onClick={() => decide.mutate({ ce: 'respinge', corp: { motiv: nota.trim() } })}
              className="rounded-caseta border border-b4l-border px-3 py-1.5 text-corp text-b4l-red-dark hover:border-b4l-red disabled:opacity-40"
            >
              Respinge
            </button>
            <button
              type="button"
              disabled={decide.isPending || nota.trim().length < 3}
              title={
                nota.trim().length < 3
                  ? 'Scrie în notă ce trebuie corectat'
                  : `${ceruteDe} o corectează și o trimite din nou`
              }
              onClick={() => decide.mutate({ ce: 'trimite-inapoi', corp: { nota: nota.trim() } })}
              className="rounded-caseta border border-b4l-amber px-3 py-1.5 text-corp text-b4l-amber-dark hover:bg-b4l-amber-light disabled:opacity-40"
            >
              Trimite înapoi
            </button>
            <button
              type="button"
              disabled={
                decide.isPending ||
                ctx.isLoading ||
                linii.length === 0 ||
                (toateZero && !nota.trim())
              }
              title={toateZero && !nota.trim() ? 'Fără nicio cantitate, scrie de ce' : undefined}
              onClick={() =>
                decide.mutate({
                  ce: 'aproba',
                  corp: {
                    linii: linii.map((l) => ({ linieId: l.linieId, cantitate: aprob(l) })),
                    motiv: nota.trim() || undefined,
                  },
                })
              }
              className="rounded-caseta bg-b4l-green px-3.5 py-1.5 text-corp font-semibold text-white hover:opacity-90 disabled:opacity-40"
            >
              {decide.isPending
                ? 'Se salvează…'
                : toateZero
                  ? 'Închide fără marfă'
                  : reduse
                    ? `Aprobă, cu ${reduse} ${reduse === 1 ? 'cantitate redusă' : 'cantități reduse'}`
                    : 'Aprobă tot'}
            </button>
          </footer>
        )}
      </aside>
    </div>
  )
}
